import { useEffect, useRef, useState } from 'react'
import DragTheWords from './DragTheWords.jsx'
import { useTranslation } from 'react-i18next'

/**
 * Previews that look like the thing being previewed. A caption in a plain box tells an owner
 * nothing about whether their post will read well in a feed; a mock of the real surface does —
 * where the text is cut off, how the picture crops, what the first line has to carry.
 */

/**
 * The domain a link points at, for the corner of a mock.
 *
 * Never a crash. A product address is whatever the catalog holds, and the catalog holds what a
 * site said about itself — one shop's links arrived without a scheme, new URL() threw, and opening
 * any post about that shop took the whole portal down to a white screen. A link that cannot be
 * parsed is shown as it is; the preview's job is to show the post, not to validate the shop.
 */
function linkHost(url) {
  for (const candidate of [url, `https://${url}`]) {
    try {
      return new URL(candidate).hostname
    } catch {
      /* the next form, then the raw text */
    }
  }

  return url
}

function initials(name) {
  return (name ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')
}

/** A caption that reads as text and becomes editable where it stands. */
function Caption({ value, disabled, onSave, className }) {
  const { t } = useTranslation()
  const [text, setText] = useState(value)
  const [saving, setSaving] = useState(false)
  const box = useRef(null)

  // Grown to what it holds, measured rather than guessed: a row count worked out from the
  // character count assumed a line width, and in a preview column narrower than the guess the
  // last line of every caption was cut in half.
  useEffect(() => {
    const field = box.current
    if (!field) return

    field.style.height = 'auto'
    field.style.height = `${field.scrollHeight}px`
  }, [text, className])

  const dirty = text !== value

  async function save() {
    setSaving(true)
    try {
      await onSave(text)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <textarea
        ref={box}
        className={className}
        value={text}
        disabled={disabled || saving}
        rows={2}
        onChange={(event) => setText(event.target.value)}
      />
      {dirty && (
        <button type="button" className="mock__save" disabled={saving} onClick={save}>
          {t('posts.save')}
        </button>
      )}
    </>
  )
}

function Placeholder({ label }) {
  return <div className="mock__placeholder">{label}</div>
}

/**
 * The pictures, as the networks show them: one image, or a strip with a dot per slide.
 *
 * Snap scrolling on a phone, because that is how a person already swipes a carousel and a preview
 * that behaves differently from the thing it previews is worth nothing. Arrows on a desk, because
 * there is nothing to swipe with a mouse — without them the second, third and fourth pictures
 * were simply unreachable, which is the whole point of having them.
 */
function Pictures({ variant, className, videoUrl, onMove, onWrite, onSet, design, pointedAt, textOf, disabled, busy }) {
  const { t } = useTranslation()
  const images = variant.imageUrls?.length ? variant.imageUrls : [variant.imageUrl].filter(Boolean)
  const [shown, setShown] = useState(0)
  const trackRef = useRef(null)

  // The network will show the video, so the preview does — muted and looping the way they
  // autoplay it, with the controls there for anyone who wants sound.
  if (videoUrl) {
    return (
      <video className={className} src={videoUrl} muted loop autoPlay playsInline controls preload="metadata" />
    )
  }

  if (images.length === 0) return <Placeholder label={t('preview.noRender')} />

  if (images.length === 1) {
    // Handles only where there is one picture to hold them: a carousel's slides scroll under the
    // finger, and a drag that fights the swipe is worse than no drag. The nine dots in the sheet
    // still move the words on those.
    if (onMove && variant.imageBoxes?.length > 0) {
      return (
        <DragTheWords
          image={images[0]}
          boxes={variant.imageBoxes}
          disabled={disabled}
          busy={busy}
          onMove={onMove}
          onWrite={onWrite}
          onSet={onSet}
          design={design}
          pointedAt={pointedAt}
          textOf={textOf}
        />
      )
    }

    return <img className={className} src={images[0]} alt="" loading="lazy" />
  }

  // Scrolled rather than jumped to, so the arrow and the swipe end in the same place and the
  // scroll handler keeps owning which slide is current.
  const go = (step) => {
    const track = trackRef.current
    if (!track) return

    const to = Math.min(Math.max(shown + step, 0), images.length - 1)
    track.scrollTo({ left: to * track.clientWidth, behavior: 'smooth' })
  }

  return (
    <div className="carousel">
      <div
        ref={trackRef}
        className="carousel__track"
        onScroll={(event) => {
          const { scrollLeft, clientWidth } = event.currentTarget
          setShown(Math.round(scrollLeft / clientWidth))
        }}
      >
        {images.map((image) => (
          <img key={image} className={`${className} carousel__slide`} src={image} alt="" loading="lazy" />
        ))}
      </div>

      <button
        type="button"
        className="carousel__arrow carousel__arrow--back"
        aria-label={t('preview.previous')}
        disabled={shown === 0}
        onClick={() => go(-1)}
      >
        ‹
      </button>

      <button
        type="button"
        className="carousel__arrow carousel__arrow--next"
        aria-label={t('preview.next')}
        disabled={shown === images.length - 1}
        onClick={() => go(1)}
      >
        ›
      </button>

      <div className="carousel__dots" aria-hidden="true">
        {images.map((image, index) => (
          <span key={image} className={`carousel__dot${index === shown ? ' on' : ''}`} />
        ))}
      </div>

      <span className="carousel__count">
        {shown + 1}/{images.length}
      </span>
    </div>
  )
}

export function FacebookCard({ business, variant, disabled, onSave, videoUrl, onMove, onWrite, onSet, design, pointedAt, textOf, busy }) {
  const { t } = useTranslation()

  return (
    <article className="mock mock--fb">
      <header className="mock__head">
        <div className="mock__avatar">{initials(business)}</div>
        <div>
          <div className="mock__name">{business}</div>
          <div className="mock__meta">
            {t('preview.justNow')} ·{' '}
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                fill="currentColor"
                d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 1.5c.9 0 1.9 1.7 2.2 4.1H5.8C6.1 4.2 7.1 2.5 8 2.5ZM3.6 6.6a5.5 5.5 0 0 1 2-3.4 11 11 0 0 0-.7 3.4Zm0 2.8h1.3c.1 1.2.3 2.4.7 3.4a5.5 5.5 0 0 1-2-3.4Zm2.2 0h4.4c-.3 2.4-1.3 4.1-2.2 4.1s-1.9-1.7-2.2-4.1Zm5.7 0h1.3a5.5 5.5 0 0 1-2 3.4c.4-1 .6-2.2.7-3.4Zm0-2.8a11 11 0 0 0-.7-3.4 5.5 5.5 0 0 1 2 3.4Z"
              />
            </svg>
          </div>
        </div>
      </header>

      <Caption className="mock__text" value={variant.text} disabled={disabled} onSave={onSave} />

      <Pictures onMove={onMove} onWrite={onWrite} onSet={onSet} design={design} pointedAt={pointedAt} textOf={textOf} disabled={disabled} busy={busy} variant={variant} className="mock__image" videoUrl={videoUrl} />

      {variant.linkUrl && <div className="mock__link">{linkHost(variant.linkUrl)}</div>}
    </article>
  )
}

export function InstagramCard({ business, variant, disabled, onSave, videoUrl, onMove, onWrite, onSet, design, pointedAt, textOf, busy }) {
  const handle = (business ?? '').toLowerCase().replace(/\s+/g, '')

  return (
    <article className="mock mock--ig">
      <header className="mock__head">
        <div className="mock__avatar mock__avatar--ring">{initials(business)}</div>
        <div className="mock__name">{handle}</div>
      </header>

      <Pictures onMove={onMove} onWrite={onWrite} onSet={onSet} design={design} pointedAt={pointedAt} textOf={textOf} disabled={disabled} busy={busy} variant={variant} className="mock__image mock__image--square" videoUrl={videoUrl} />

      <div className="mock__caption">
        <span className="mock__handle">{handle}</span>
        <Caption className="mock__text mock__text--ig" value={variant.text} disabled={disabled} onSave={onSave} />
      </div>

      {variant.hashtags?.length > 0 && <div className="mock__tags">{variant.hashtags.join(' ')}</div>}
    </article>
  )
}

/** A story is a full-bleed 9:16 frame with nothing around it but the ring at the top. */
export function StoryCard({ business, variant, videoUrl, onMove, onWrite, onSet, design, pointedAt, textOf, disabled, busy }) {
  const { t } = useTranslation()

  return (
    <article className="mock mock--story">
      <div className="mock__storyBar" aria-hidden="true" />
      <header className="mock__head mock__head--story">
        <div className="mock__avatar mock__avatar--ring">{initials(business)}</div>
        <div className="mock__name">{business}</div>
      </header>

      {videoUrl || variant.imageUrl ? (
        <Pictures onMove={onMove} onWrite={onWrite} onSet={onSet} design={design} pointedAt={pointedAt} textOf={textOf} disabled={disabled} busy={busy} variant={variant} className="mock__image mock__image--story" videoUrl={videoUrl} />
      ) : (
        <Placeholder label={t('preview.noRender')} />
      )}
    </article>
  )
}
